// The fleet effect recipes, mockup round 041 (R924).
//
// One entry per catalog code the owner picked for a hull, keyed by the code
// of `data/contracts/effects.toml`. `effect-styles.ts` prefers this map over
// the row's family, so a code that is not here yet draws its family and the
// map fills in one card at a time. All twenty fleet codes are here: the
// nineteen of R924 and `frost`, which the owner added later (R960).
//
// **A recipe reads its figures out of the row and holds no literal.** The
// row carries `periodSec`, `radiusPx`, `alpha` and `count`, plus the six
// figure fields `radius2Px`, `widthPx`, `lengthPx`, `alpha2`, `speed` and
// `count2`; `data/presentation/effects.toml` names what each one means for
// each card. A PROPORTION is not a figure: how far a breath swells a
// radius, or how wide an arc opens, stays here as a module constant, the
// way `RAY_INNER` does in the kit.
//
// **A recipe is a pure function of the clock** (R915). It reads `c.t` and
// nothing else that moves, it keeps no state between frames, and it
// scatters with `grain(id, k)`. It allocates nothing (R131), and it clamps
// every alpha through `fill()` or `stroke()`.
//
// **How a card becomes instances.** The mockup draws on a 2D canvas and this
// draws into three instanced batches, so the translation is fixed:
//
//   - the card's `flare` (a radial gradient) is the `effect:disc` sprite,
//     scaled to the radius and drawn additive: `glow` of the kit;
//   - the card's `fogBlob` is the `effect:fog` sprite;
//   - the card's hard `disc` is a circle instance, drawn NOT additive, so a
//     white core over a lit glow stays a core and does not go white-hot:
//     `core` of the kit;
//   - the card's `line`, `rays` and strand paths are segment instances;
//   - the card's `ring` and arc are circle instances.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import { EFFECT_LIMITS } from '../gen/presentation';
import {
  TAU, breathe, cellScale, core, cycle, effectCellKey, fill, glow, grain, mixWhite, stroke,
  type EffectRecipeFn,
} from './effect-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------
//
// Every figure in world px or in seconds comes from the row. What is below
// is the SHAPE of one animation: how far a breath swells a radius, how deep
// a resting alpha sits under the peak, how wide an arc opens. The card each
// one comes from is named, and the arithmetic that recovers it from the
// card's own numbers is in the comment.

/** halo, card 02: the disc runs 0.88 to 1.12 of the reach over one breath. */
const HALO_SWELL = 0.88, HALO_SPAN = 0.24;
/** halo: the alpha at the bottom of the breath, as a share of the peak. */
const HALO_REST = 0.55;

/** stardust_wake, card 01: the smallest grain, as a share of the widest. */
const STARDUST_GRAIN = 0.54;
/** stardust_wake: the dimmest grain, as a share of the brightest. */
const STARDUST_DIM = 0.4;
/** stardust_wake: how much of the scatter disc reaches off the wake line. */
const STARDUST_LATERAL = 0.9;

/** twin_sprites, card 06: the orbit is drawn as a flat ellipse. */
const ORBIT_SQUASH = 0.5;
/** twin_sprites: the hard core, as a share of the pip's own glow. */
const TWIN_CORE = 0.24;

/** third_eye, card 09: the ring runs 0.92 to 1.08 of the reach. */
const EYE_SWELL = 0.92, EYE_SPAN = 0.16;
/** third_eye: the stroke at the bottom of the breath, as a share of alpha2. */
const EYE_REST = 0.58;

/** lantern, card 10: the glow runs 0.82 to 1.18 of the reach. */
const LANTERN_SWELL = 0.82, LANTERN_SPAN = 0.36;
/** lantern: the glow at the bottom of the breath, as a share of the peak. */
const LANTERN_REST = 0.5;

/** silt_plume, card 12: a cloud is this wide when it leaves the hull. */
const SILT_START = 0.44;

/** bow_shimmer, card 14: the V's half width at the stern, as a share of
 *  the width it opens to. */
const BOW_START = 0.125;
/** bow_shimmer: one grain, as a share of the width the V opens to. */
const BOW_GRAIN = 0.07;
/** bow_shimmer: the whole line sits under the row's peak by this share. */
const BOW_DIM = 0.9;

/** kelp_drift, card 16: where a strand leaves the hull, as a share of the
 *  run astern. */
const KELP_START = 0.13;
/** kelp_drift: the strand's resting offset, as a share of the sway reach. */
const KELP_SPREAD = 0.375;
/** kelp_drift: how many radians of the wave stand on the run. */
const KELP_WAVES = 2.6;
/** kelp_drift: how far the second strand lags the first, in radians. */
const KELP_LAG = 1.1;

/** lantern_chain, card 20: the sway, as a share of one pip's glow. */
const CHAIN_SWAY = 0.45;
/** lantern_chain: how far each pip lags the pip in front, in radians. */
const CHAIN_LAG = 0.7;
/** lantern_chain: how much dimmer each pip is than the pip in front. */
const CHAIN_DIM = 0.28;
/** lantern_chain: the hard core, as a share of one pip's glow. */
const CHAIN_CORE = 0.22;

/** compass_rose, card 21: where a point starts, as a share of its reach. */
const ROSE_INNER = 0.233;
/** compass_rose: the hub ring, as a share of the long point's reach. */
const ROSE_HUB = 0.34;
/** compass_rose: the short point's stroke, as a share of the long one's. */
const ROSE_SHORT_W = 0.77;

/** bubble_breath, card 22: the smallest glow, as a share of the widest. */
const BUBBLE_GLOW = 0.6;
/** bubble_breath: the smallest bubble, as a share of the widest. */
const BUBBLE_MIN = 0.435;
/** bubble_breath: how far the leaving disc reaches along and across. */
const BUBBLE_ALONG = 0.7, BUBBLE_ACROSS = 0.5;

/** corsair_blaze, card 24: the flare runs 0.78 to 1.22 of the reach. */
const BLAZE_SWELL = 0.78, BLAZE_SPAN = 0.44;
/** corsair_blaze: the flare at the bottom of the breath. */
const BLAZE_REST = 0.5;

/** cutlass_glint, card 25: the run along the edge, as a share of the run
 *  length: the shoulder is at (X0, Y0) and the tip at (X1, Y1). */
const CUTLASS_X0 = 0.25, CUTLASS_Y0 = 0.4;
const CUTLASS_X1 = -0.65, CUTLASS_Y1 = 0.9;
/** cutlass_glint: what share of the period the run takes; the rest is dark. */
const CUTLASS_RUN = 0.6;
/** cutlass_glint: the shortest glint arm, as a share of the longest. */
const CUTLASS_ARM = 0.375;
/** cutlass_glint: the glow under the glint, as a share of the row's peak. */
const CUTLASS_GLOW = 0.67;

/** falconet_feathers, card 26: where a streak starts, as a share of the run. */
const FEATHER_START = 0.12;
/** falconet_feathers: one streak's length, as a share of the run. */
const FEATHER_LEN = 0.16;
/** falconet_feathers: the fin's sway, as a share of the fin offset. */
const FEATHER_WAVE = 0.54;
/** falconet_feathers: how far a streak's far end splays out. */
const FEATHER_SPLAY = 0.34;
/** falconet_feathers: how many radians of the wave stand on the run. */
const FEATHER_TURNS = 3.1;
/** falconet_feathers: the thinnest streak, as a share of the widest. */
const FEATHER_MIN_W = 0.41;

/** basilisk_gaze, card 27: the cone runs 0.82 to 1.14 of the reach. */
const GAZE_SWELL = 0.82, GAZE_SPAN = 0.32;
/** basilisk_gaze: the cone at the bottom of the breath. */
const GAZE_REST = 0.5;
/** basilisk_gaze: where the beam's brightest part stands on the run. The
 *  card's gradient is brightest at the mouth and clear at the far end, and
 *  a baked cell is brightest at its own middle, so the middle moves toward
 *  the bow and the beam then reads as one piece with the hull. */
const GAZE_CENTRE = 0.4;

/** sentinel_watch, card 28: the arc's half angle, in radians. */
const SENTINEL_HALF = 0.5;
/** sentinel_watch: the track under the arc, as a share of the arc's alpha. */
const SENTINEL_TRACK = 0.22;
/** sentinel_watch: the track's stroke, as a share of the arc's. */
const SENTINEL_TRACK_W = 0.625;

/** lancer_spearhead, card 29: the share of the run the pip fades in over. */
const LANCE_RAMP = 0.12;
/** lancer_spearhead: the hard core, as a share of the pip's glow. */
const LANCE_CORE = 0.22;

/** frost, card 07: how flat the band of glints lies across the hull. */
const FROST_FLAT = 0.7;
/** frost: the shortest glint arm, as a share of the longest. */
const FROST_ARM = 0.368;

/** beacon_crown, card 30: the unlit pip's glow, as a share of the lit one. */
const CROWN_GLOW = 0.286;
/** beacon_crown: the hard core, as a share of the lit pip's glow. */
const CROWN_CORE = 0.17;
/** beacon_crown: the unlit core, as a share of the lit one. */
const CROWN_REST = 0.4;

/**
 * The fleet recipes, by catalog code.
 *
 * Every code of R924's fleet list and the one code of R960 is here, so no
 * fleet row takes the family fallback. The comment over each one names its
 * mockup card.
 */
export const RECIPES: Readonly<Record<string, EffectRecipeFn>> = {
  // 041 card 02. One soft disc under the mark. The breath swells the reach
  // and lifts the alpha off a resting floor, so the mark never blinks out.
  halo(row, c) {
    const k = breathe(c.t, row.periodSec, c.phase);
    const e = c.sprite(effectCellKey('disc'));
    glow(c, e, c.x, c.y, cellScale(e, row.radiusPx * (HALO_SWELL + HALO_SPAN * k)),
      row.alpha * (HALO_REST + (1 - HALO_REST) * k));
  },

  // 041 card 01. Faint grains shed astern. Each grain owns its place in the
  // scatter disc, its size and its age, and it twinkles once over the
  // period as it runs from the stern to the end of the wake.
  stardust_wake(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    for (let k = 0; k < row.count; k++) {
      const h0 = grain(c.id, 3 * k + 1);
      const h1 = grain(c.id, 3 * k + 2);
      const h2 = grain(c.id, 3 * k + 3);
      const age = cycle(c.t, row.periodSec, h0);
      const d = row.radius2Px + age * row.lengthPx;
      const off = Math.sin(h1 * TAU) * Math.sqrt(h2) * row.radiusPx * STARDUST_LATERAL;
      const f = Math.sin(Math.PI * age);
      const r = row.widthPx * (STARDUST_GRAIN + (1 - STARDUST_GRAIN) * h1);
      glow(c, e, c.x - cos * d - sin * off, c.y - sin * d + cos * off, cellScale(e, r),
        row.alpha * f * (STARDUST_DIM + (1 - STARDUST_DIM) * h1));
    }
  },

  // 041 card 03. The rim round the silhouette drifts from the faction ink
  // toward white over the period, and back. The card stroked the hull path
  // itself; the layer has no path, so the row states the rim's reach.
  ember_rim(row, c) {
    const k = EFFECT_LIMITS.maxDriftTowardWhite * breathe(c.t, row.periodSec, c.phase);
    c.rings.ring(
      c.x, c.y, row.radiusPx, row.widthPx, mixWhite(c.color, k),
      stroke(row.alpha2) * c.alpha,
    );
  },

  // 041 card 06. Two pips orbit the hull on a flat ellipse, one revolution
  // a period, each with a soft glow and a hard core.
  twin_sprites(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const s = cellScale(e, row.radius2Px);
    const u = cycle(c.t, row.periodSec, c.phase);
    for (let k = 0; k < row.count; k++) {
      const th = (u + k / row.count) * TAU;
      const px = c.x + Math.cos(th) * row.radiusPx;
      const py = c.y + Math.sin(th) * row.radiusPx * ORBIT_SQUASH;
      glow(c, e, px, py, s, row.alpha);
      core(c, px, py, row.radius2Px * TWIN_CORE, row.alpha2);
    }
  },

  // 041 card 09. One thin ring stands round the Oracle's needle and
  // breathes: the reach and the stroke both ride the breath.
  third_eye(row, c) {
    const k = breathe(c.t, row.periodSec, c.phase);
    c.rings.ring(
      c.x, c.y, row.radiusPx * (EYE_SWELL + EYE_SPAN * k), row.widthPx, c.color,
      stroke(row.alpha2 * (EYE_REST + (1 - EYE_REST) * k)) * c.alpha,
    );
  },

  // 041 card 10. A glow hangs under the Tender on a short cable. The cable
  // runs down the map, not down the hull, the way a hanging light does.
  lantern(row, c) {
    const k = breathe(c.t, row.periodSec, c.phase);
    const y1 = c.y + row.radius2Px;
    c.segs.line(c.x, y1 - row.lengthPx, c.x, y1, row.widthPx, c.color,
      stroke(row.alpha2) * c.alpha);
    const e = c.sprite(effectCellKey('disc'));
    glow(c, e, c.x, y1, cellScale(e, row.radiusPx * (LANTERN_SWELL + LANTERN_SPAN * k)),
      row.alpha * (LANTERN_REST + (1 - LANTERN_REST) * k));
  },

  // 041 card 12. Silt clouds leave the Dredger astern, grow over the run
  // and thin away. Each cloud takes its own place across the wake.
  silt_plume(row, c) {
    const e = c.sprite(effectCellKey('fog'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    for (let k = 0; k < row.count; k++) {
      const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
      const d = row.radius2Px + u * row.lengthPx;
      const off = (grain(c.id, k + 1) - 0.5) * row.radius2Px;
      glow(
        c, e, c.x - cos * d - sin * off, c.y - sin * d + cos * off,
        cellScale(e, row.radiusPx * (SILT_START + u)),
        row.alpha * Math.sin(Math.PI * u),
      );
    }
  },

  // 041 card 14. Two lines of grains open astern in a V, the way a lit bow
  // wave opens. A grain brightens at the middle of its run and goes out.
  bow_shimmer(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const s = cellScale(e, row.widthPx * BOW_GRAIN);
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    for (let line = 0; line < row.count2; line++) {
      const side = (line & 1) === 0 ? 1 : -1;
      for (let k = 0; k < row.count; k++) {
        const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
        const d = row.radius2Px + u * row.radiusPx;
        const off = side * row.widthPx * (BOW_START + u);
        glow(c, e, c.x - cos * d - sin * off, c.y - sin * d + cos * off, s,
          row.alpha * Math.sin(Math.PI * u) * BOW_DIM);
      }
    }
  },

  // 041 card 16. Thin strands trail astern and sway, the way kelp sways in
  // a slow current. One strand is a run of segments; the sway grows with
  // the distance from the hull, so the root holds and the tip moves.
  kelp_drift(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const w = cycle(c.t, row.periodSec, c.phase) * TAU;
    const start = row.radiusPx * KELP_START;
    const a = stroke(row.alpha2) * c.alpha;
    for (let strand = 0; strand < row.count; strand++) {
      const side = (strand & 1) === 0 ? 1 : -1;
      const lag = strand * KELP_LAG;
      let px = 0, py = 0;
      for (let i = 0; i <= row.count2; i++) {
        const u = i / row.count2;
        const d = start + u * row.radiusPx;
        const off = side * row.radius2Px * KELP_SPREAD
          + Math.sin(w + u * KELP_WAVES + lag) * row.radius2Px * u;
        const nx = c.x - cos * d - sin * off;
        const ny = c.y - sin * d + cos * off;
        if (i > 0) c.segs.line(px, py, nx, ny, row.widthPx, c.color, a);
        px = nx; py = ny;
      }
    }
  },

  // 041 card 20. White pips trail astern in a line, each dimmer than the
  // pip in front of it, and the whole chain sways over the period.
  lantern_chain(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const s = cellScale(e, row.radiusPx);
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const w = cycle(c.t, row.periodSec, c.phase) * TAU;
    for (let k = 0; k < row.count; k++) {
      const d = row.radius2Px + k * row.lengthPx
        + Math.sin(w + k * CHAIN_LAG) * row.radiusPx * CHAIN_SWAY;
      const f = Math.max(0, 1 - k * CHAIN_DIM);
      const px = c.x - cos * d, py = c.y - sin * d;
      glow(c, e, px, py, s, row.alpha * f);
      core(c, px, py, row.radiusPx * CHAIN_CORE, row.alpha2 * f);
    }
  },

  // 041 card 21. A faint star lies under the hull and turns one revolution
  // a period: long points, short points between them, and a hub ring.
  compass_rose(row, c) {
    const rot = cycle(c.t, row.periodSec, c.phase) * TAU;
    const r0 = row.radiusPx * ROSE_INNER;
    const along = stroke(row.alpha) * c.alpha;
    for (let k = 0; k < row.count; k++) {
      const th = rot + (k / row.count) * TAU;
      const cos = Math.cos(th), sin = Math.sin(th);
      c.segs.line(c.x + cos * r0, c.y + sin * r0,
        c.x + cos * row.radiusPx, c.y + sin * row.radiusPx,
        row.widthPx, c.color, along);
    }
    const short = stroke(row.alpha2) * c.alpha;
    const shortW = row.widthPx * ROSE_SHORT_W;
    for (let k = 0; k < row.count2; k++) {
      const th = rot + Math.PI / row.count2 + (k / row.count2) * TAU;
      const cos = Math.cos(th), sin = Math.sin(th);
      c.segs.line(c.x + cos * r0, c.y + sin * r0,
        c.x + cos * row.radius2Px, c.y + sin * row.radius2Px,
        shortW, c.color, short);
    }
    c.rings.ring(c.x, c.y, row.radiusPx * ROSE_HUB, shortW, c.color, short);
  },

  // 041 card 22. Small bubbles leave the hull a few at a time and rise off
  // the track. Each bubble is a hard core inside a very faint glow.
  bubble_breath(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    for (let k = 0; k < row.count; k++) {
      const h0 = grain(c.id, 3 * k + 1);
      const h1 = grain(c.id, 3 * k + 2);
      const h2 = grain(c.id, 3 * k + 3);
      const age = cycle(c.t, row.periodSec, h0);
      const ang = h1 * TAU;
      const rad = Math.sqrt(h2) * row.radiusPx;
      const px = c.x + Math.cos(ang) * rad * BUBBLE_ALONG + (h1 - 0.5) * row.radius2Px;
      const py = c.y + Math.sin(ang) * rad * BUBBLE_ACROSS - age * row.lengthPx;
      const f = Math.sin(Math.PI * age);
      glow(
        c, e, px, py,
        cellScale(e, row.radius2Px * (BUBBLE_GLOW + (1 - BUBBLE_GLOW) * h1)),
        row.alpha2 * f,
      );
      core(c, px, py, row.widthPx * (BUBBLE_MIN + (1 - BUBBLE_MIN) * h1),
        row.alpha * f);
    }
  },

  // 041 card 24. A soft flare sits ahead of the Corsair's bow and breathes.
  corsair_blaze(row, c) {
    const k = breathe(c.t, row.periodSec, c.phase);
    const e = c.sprite(effectCellKey('disc'));
    const bx = c.x + Math.cos(c.heading) * row.radius2Px;
    const by = c.y + Math.sin(c.heading) * row.radius2Px;
    glow(c, e, bx, by, cellScale(e, row.radiusPx * (BLAZE_SWELL + BLAZE_SPAN * k)),
      row.alpha * (BLAZE_REST + (1 - BLAZE_REST) * k));
  },

  // 041 card 25. A glint runs along each blade edge of the Cutlass, from
  // the shoulder to the tip, and the edges rest dark for the rest of the
  // period. The card drew the glint as two crossed hairlines; the engine
  // bakes that star as `effect:glint`, so the row's brighter figure is a
  // fill here and takes the fill ceiling.
  cutlass_glint(row, c) {
    const u = cycle(c.t, row.periodSec, c.phase);
    if (u > CUTLASS_RUN) return;
    const s = u / CUTLASS_RUN;
    const f = Math.sin(Math.PI * s);
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const soft = c.sprite(effectCellKey('disc'));
    const star = c.sprite(effectCellKey('glint'));
    const gs = cellScale(soft, row.radiusPx);
    const ss = cellScale(star, row.radius2Px * (CUTLASS_ARM + (1 - CUTLASS_ARM) * f));
    const lx = row.lengthPx * (CUTLASS_X0 + (CUTLASS_X1 - CUTLASS_X0) * s);
    const ly = row.lengthPx * (CUTLASS_Y0 + (CUTLASS_Y1 - CUTLASS_Y0) * s);
    for (let k = 0; k < row.count; k++) {
      const side = (k & 1) === 0 ? 1 : -1;
      const px = c.x + lx * cos - side * ly * sin;
      const py = c.y + lx * sin + side * ly * cos;
      glow(c, soft, px, py, gs, row.alpha * CUTLASS_GLOW * f);
      glow(c, star, px, py, ss, row.alpha2 * f);
    }
  },

  // 041 card 26. Feather streaks leave the Falconet's fins astern and fade.
  // A streak is one short segment that thins as it runs.
  falconet_feathers(row, c) {
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    for (let finK = 0; finK < row.count2; finK++) {
      const side = (finK & 1) === 0 ? 1 : -1;
      for (let k = 0; k < row.count; k++) {
        const u = cycle(c.t, row.periodSec, c.phase + k / row.count);
        const d = row.radiusPx * FEATHER_START + u * row.radiusPx;
        const off = side * (row.radius2Px
          + Math.sin(u * FEATHER_TURNS) * row.radius2Px * FEATHER_WAVE);
        const off2 = off + side * row.radius2Px * FEATHER_SPLAY;
        const d2 = d + row.radiusPx * FEATHER_LEN;
        c.segs.line(
          c.x - cos * d - sin * off, c.y - sin * d + cos * off,
          c.x - cos * d2 - sin * off2, c.y - sin * d2 + cos * off2,
          Math.max(row.widthPx * FEATHER_MIN_W, row.widthPx * (1 - u)),
          c.color, stroke(row.alpha * (1 - u)) * c.alpha,
        );
      }
    }
  },

  // 041 card 27. A narrow faint cone stands ahead of the Basilisk's bow and
  // breathes. The card filled a gradient triangle; a batch has no triangle,
  // so this stretches the soft `effect:fog` cell along the heading, which
  // gives the same faint beam with no edge.
  basilisk_gaze(row, c) {
    const k = breathe(c.t, row.periodSec, c.phase);
    const len = row.radiusPx * (GAZE_SWELL + GAZE_SPAN * k);
    const e = c.sprite(effectCellKey('fog'));
    const mid = row.radius2Px + len * GAZE_CENTRE;
    c.quads.quad(
      c.x + Math.cos(c.heading) * mid, c.y + Math.sin(c.heading) * mid, 0, 0,
      len, 2 * row.widthPx * (len / row.radiusPx), c.heading, c.add, c.color,
      fill(row.alpha * (GAZE_REST + (1 - GAZE_REST) * k)) * c.alpha,
      e.u0, e.v0, e.u1, e.v1,
    );
  },

  // 041 card 28. A short arc circles the Sentinel once a period on a very
  // faint track.
  sentinel_watch(row, c) {
    const th = cycle(c.t, row.periodSec, c.phase) * TAU;
    c.rings.ring(c.x, c.y, row.radiusPx, row.widthPx * SENTINEL_TRACK_W, c.color,
      stroke(row.alpha2 * SENTINEL_TRACK) * c.alpha);
    c.rings.arc(c.x, c.y, row.radiusPx, row.widthPx,
      th - SENTINEL_HALF, 2 * SENTINEL_HALF, c.color,
      stroke(row.alpha2) * c.alpha);
  },

  // 041 card 29. One pip runs from the Lancer's stern to the tip every
  // period, and then the run resets. It fades in and out at the two ends.
  lancer_spearhead(row, c) {
    const u = cycle(c.t, row.periodSec, c.phase);
    const d = -row.radius2Px + u * row.lengthPx;
    const f = Math.min(1, u / LANCE_RAMP) * Math.min(1, (1 - u) / LANCE_RAMP);
    const px = c.x + Math.cos(c.heading) * d;
    const py = c.y + Math.sin(c.heading) * d;
    const e = c.sprite(effectCellKey('disc'));
    glow(c, e, px, py, cellScale(e, row.radiusPx), row.alpha * f);
    core(c, px, py, row.radiusPx * LANCE_CORE, row.alpha2 * f);
  },

  // 041 card 07. White glints twinkle on the outline of the hull alone
  // (R960). A glint owns its place in a band round the silhouette, its arm
  // and its age, and it twinkles one time over the period. The card drew
  // two crossed hairlines; the engine bakes that star as `effect:glint`.
  frost(row, c) {
    const e = c.sprite(effectCellKey('glint'));
    const cos = Math.cos(c.heading), sin = Math.sin(c.heading);
    const band = row.radiusPx - row.radius2Px;
    for (let k = 0; k < row.count; k++) {
      const h0 = grain(c.id, 3 * k + 1);
      const h1 = grain(c.id, 3 * k + 2);
      const h2 = grain(c.id, 3 * k + 3);
      const age = cycle(c.t, row.periodSec, h0);
      const th = h1 * TAU;
      const d = row.radius2Px + h2 * band;
      const lx = Math.cos(th) * d, ly = Math.sin(th) * d * FROST_FLAT;
      const f = Math.sin(Math.PI * age);
      glow(c, e, c.x + lx * cos - ly * sin, c.y + lx * sin + ly * cos,
        cellScale(e, row.widthPx * (FROST_ARM + (1 - FROST_ARM) * f)), row.alpha * f);
    }
  },

  // 041 card 30. Pips stand round the Beacon and light in turn. A pip is
  // lit for one share of the period, and its core never goes out.
  beacon_crown(row, c) {
    const e = c.sprite(effectCellKey('disc'));
    const share = 1 / row.count;
    for (let k = 0; k < row.count; k++) {
      const th = (k * share + c.phase) * TAU;
      const u = cycle(c.t, row.periodSec, c.phase - k * share);
      const lit = u < share ? Math.sin(Math.PI * u / share) : 0;
      const px = c.x + Math.cos(th) * row.radiusPx;
      const py = c.y + Math.sin(th) * row.radiusPx;
      glow(
        c, e, px, py,
        cellScale(e, row.radius2Px * (CROWN_GLOW + (1 - CROWN_GLOW) * lit)),
        row.alpha * lit,
      );
      core(c, px, py, row.radius2Px * CROWN_CORE,
        row.alpha2 * (CROWN_REST + (1 - CROWN_REST) * lit));
    }
  },
};
